import { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Beer, Circle, CupSoda, Footprints, FlipHorizontal2, Hammer, Lock, Magnet, Music, Radio, Square, Unlock, Trash2, Wind } from 'lucide-react';
import { useTools } from '../../../../ToolProvider.jsx';
import { useDataModelo3dContext } from '../../../../DataProvider.jsx';
import InputSpinner from '../InputSpinner2/InputSpinner2.jsx';
import {
    ABRELATAS_DEFAULTS,
    ABRELATAS_LIMITS,
    ABRELATAS_MIN_EDGE_MM,
    ABRELATAS_MIN_ROOF_MM,
    ABRELATAS_OVERSHOOT_COLOR,
    ABRELATAS_SHAPES,
    ABRIDOR_DEFAULTS,
    ABRIDOR_LIMITS,
    ABRIDOR_POCKET_COLORS,
    ABRIDOR_POCKET_SHAPES,
    ABRIDOR_PROFILE_POINTS,
    ABRIDOR_WASHERS,
    abridorDepthColor,
    SILBATO_DEFAULTS,
    SILBATO_LIMITS,
    buildAbridorGeometry,
    buildSilbatoGeometry,
    buildAbrelatasGeometry,
    sampleAbridorProfile,
} from '../../../../../../fabricjs/editor.js';

const PRESETS = {
    magnet: { sizeMm: 6, heightMm: 6, thicknessMm: 2, bottomOffsetMm: 0.8 },
    nfc: { sizeMm: 25, heightMm: 25, thicknessMm: 0.8, bottomOffsetMm: 0.8 },
    silbato: { ...SILBATO_DEFAULTS },
    abridor: { ...ABRIDOR_DEFAULTS },
    abrelatas: { ...ABRELATAS_DEFAULTS },
};

// Suelo mínimo bajo el rebaje del abrebotellas; el backend avisa con lo mismo.
const ABRIDOR_MIN_FLOOR_MM = 1.2;
// Material mínimo entre la arandela y la cara trasera: lo que la retiene al
// hacer palanca. Mismo valor que `ABRIDOR_MIN_COVER_MM` del backend.
const ABRIDOR_MIN_COVER_MM = 2;

// Los campos del silbato, agrupados por la vía de aire a la que pertenecen.
// El orden va del final del recorrido al principio porque es el orden en que se
// ajusta a oído: primero el tono (la cámara), después el timbre (el labio) y
// sólo al final la facilidad de soplado.
const SILBATO_SECTIONS = [
    {
        title: 'Cámara de resonancia',
        hint: 'Es la que manda en el tono: más larga, más grave.',
        fields: [
            { key: 'chamberLengthMm', label: 'Largo (mm)', step: 1 },
            { key: 'chamberWidthMm', label: 'Ancho (mm)', step: 0.5 },
        ],
    },
    {
        title: 'Labio',
        hint: 'Parte el chorro contra la cámara. El ángulo decide si el silbato habla limpio o sopla.',
        fields: [
            { key: 'lipAngleDeg', label: 'Ángulo (°)', step: 0.5 },
            { key: 'lipLengthMm', label: 'Largo (mm)', step: 1 },
            { key: 'lipThicknessMm', label: 'Grosor (mm)', step: 0.5 },
        ],
    },
    {
        title: 'Salida de aire',
        hint: 'La ventana por la que sale el sonido.',
        fields: [
            { key: 'outletLengthMm', label: 'Largo (mm)', step: 1 },
            { key: 'outletWidthMm', label: 'Ancho (mm)', step: 0.5 },
        ],
    },
    {
        title: 'Entrada de aire',
        hint: 'Canal estrecho hasta el labio, y la rampa por la que se sopla.',
        fields: [
            { key: 'inletWidthMm', label: 'Ancho del canal (mm)', step: 0.2 },
            { key: 'inletLengthMm', label: 'Largo del canal (mm)', step: 1 },
            { key: 'rampAngleDeg', label: 'Ángulo de rampa (°)', step: 0.5 },
            { key: 'rampLengthMm', label: 'Largo de rampa (mm)', step: 1 },
            { key: 'rampMouthWidthMm', label: 'Boca de rampa (mm)', step: 0.5 },
        ],
    },
];

const SILBATO_VALUE_SHAPE = PropTypes.shape(
    Object.fromEntries(Object.keys(SILBATO_DEFAULTS).map(key => [
        key,
        key === 'mirrored' ? PropTypes.bool : PropTypes.number,
    ])),
);

/**
 * Ancho y alto de una pieza cuadrada/rectangular, con el mismo candado que el
 * fondo: bloqueado, tocar una cota arrastra la otra manteniendo la proporción.
 * En circular sólo hay diámetro.
 */
function SizeFields({ shape, width, height, locked, onLockChange, onChange }) {
    const round = (value) => Math.round(value * 10) / 10;
    const clamp = (value) => Math.min(Math.max(value, 2), 60);

    if (shape === 'circle') {
        return (
            <InputSpinner
                label="Diámetro (mm)"
                value={width}
                onChange={(sizeMm) => onChange({ sizeMm, heightMm: sizeMm })}
                min={2}
                max={60}
                initialStep={0.5}
            />
        );
    }

    const changeWidth = (sizeMm) => onChange(locked && width
        ? { sizeMm, heightMm: clamp(round(sizeMm * height / width)) }
        : { sizeMm });
    const changeHeight = (heightMm) => onChange(locked && height
        ? { heightMm, sizeMm: clamp(round(heightMm * width / height)) }
        : { heightMm });

    return (
        <div className="col-span-2 flex items-end gap-2">
            <div className="min-w-0 flex-1">
                <InputSpinner label="Ancho (mm)" value={width} onChange={changeWidth} min={2} max={60} initialStep={0.5} />
            </div>
            <button
                type="button"
                onClick={() => onLockChange(!locked)}
                title={locked ? 'Proporción bloqueada (click para desbloquear)' : 'Proporción libre (click para bloquear)'}
                aria-pressed={locked}
                className={`btn btn-sm mb-0.5 px-2 ${locked ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
            >
                {locked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
            </button>
            <div className="min-w-0 flex-1">
                <InputSpinner label="Alto (mm)" value={height} onChange={changeHeight} min={2} max={60} initialStep={0.5} />
            </div>
        </div>
    );
}

SizeFields.propTypes = {
    shape: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    locked: PropTypes.bool.isRequired,
    onLockChange: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
};

const KIND_LABELS = { nfc: 'NFC', silbato: 'Silbato', abridor: 'Abrebotellas', abrelatas: 'Abrelatas', magnet: 'Imán' };
// Los que llevan sus propios campos y su propio aviso de material.
const ASSEMBLY_KINDS = new Set(['silbato', 'abridor', 'abrelatas']);
const labelFor = (element) => KIND_LABELS[element.kind] ?? KIND_LABELS.magnet;

/**
 * Nota estimada del silbato, resuelta en el backend.
 *
 * Va detrás de un botón y no se recalcula al mover una cota: resolver los modos
 * de la cavidad tarda algo más de un segundo, y hacerlo en cada pulsación
 * dejaría el panel colgado.
 *
 * Se manda el lienzo tal cual está, sin guardar, porque lo que interesa es la
 * cota que se acaba de tocar.
 */
function SilbatoNote({ index }) {
    const { getDesignSVG } = useTools();
    const dataModelo3d = useDataModelo3dContext();
    const [state, setState] = useState({ status: 'idle' });

    const estimate = async () => {
        const svg = getDesignSVG?.();
        if (!svg) {
            setState({ status: 'error', message: 'No se pudo leer el diseño.' });
            return;
        }
        setState({ status: 'loading' });
        try {
            const { data } = await axios.post('customized-keyrings/silbato-notes/', {
                svg,
                width: dataModelo3d?.baseLayout?.width,
                height: dataModelo3d?.baseLayout?.height,
            });
            const silbato = data?.silbatos?.find(item => item.index === index) ?? data?.silbatos?.[0];
            if (!silbato) {
                setState({ status: 'error', message: 'El backend no encontró el silbato.' });
                return;
            }
            setState({ status: 'done', silbato });
        } catch (error) {
            setState({
                status: 'error',
                message: error.response?.data?.detail || 'No se pudo calcular la nota.',
            });
        }
    };

    const silbato = state.silbato;
    const fundamental = silbato?.modes?.[0];
    const second = silbato?.modes?.[1];
    // Por debajo de ×3 el segundo modo queda al alcance de un soplido fuerte.
    const overblows = fundamental && second
        && second.frequency_hz / fundamental.frequency_hz < 3;

    return (
        <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide opacity-70">Nota estimada</h4>
            <button
                type="button"
                className="btn btn-outline btn-sm mt-2 w-full gap-2"
                onClick={estimate}
                disabled={state.status === 'loading'}
            >
                {state.status === 'loading'
                    ? <span className="loading loading-spinner loading-xs" />
                    : <Music className="h-4 w-4" />}
                {state.status === 'loading' ? 'Resolviendo la cavidad...' : 'Calcular nota'}
            </button>

            {state.status === 'error' && (
                <p className="mt-2 rounded bg-error/15 px-2 py-1.5 text-xs text-error">{state.message}</p>
            )}

            {state.status === 'done' && (
                <div className="mt-2 flex flex-col gap-1.5">
                    {silbato.sealed ? (
                        <p className="rounded bg-error/15 px-2 py-1.5 text-xs text-error">
                            El silbato está sellado: no llega al borde de la placa, así que no va a
                            sonar. Saca la ranura de salida y el labio fuera del fondo.
                        </p>
                    ) : (
                        <div className="rounded bg-base-100 px-2 py-1.5">
                            <span className="text-lg font-semibold">{fundamental.note}</span>
                            <span className="ml-2 text-xs opacity-60">
                                {fundamental.frequency_hz} Hz · {fundamental.cents >= 0 ? '+' : ''}
                                {fundamental.cents} cents
                            </span>
                        </div>
                    )}

                    {silbato.split_pockets > 0 && (
                        <p className="rounded bg-warning/15 px-2 py-1.5 text-xs text-warning">
                            La cavidad está partida en {silbato.split_pockets + 1} trozos: hay material
                            cortando el paso del aire.
                        </p>
                    )}

                    {overblows && (
                        <p className="rounded bg-warning/15 px-2 py-1.5 text-xs text-warning">
                            El 2º modo ({second.note}) está a ×{(second.frequency_hz / fundamental.frequency_hz).toFixed(2)}:
                            soplando fuerte saltará a él.
                        </p>
                    )}

                    <p className="text-xs opacity-55">
                        Sin calibrar. Sale de resolver los modos de la cavidad a 35 °C, y todavía
                        no se ha comparado con ningún silbato medido.
                    </p>
                </div>
            )}
        </section>
    );
}

SilbatoNote.propTypes = { index: PropTypes.number };

function SilbatoFields({ values, onChange, totalThickness, index }) {
    const geometry = useMemo(() => buildSilbatoGeometry(values), [values]);
    const topCover = totalThickness - values.floorMm - values.ductMm;
    const spinner = (key, label, step) => (
        <InputSpinner
            key={key}
            label={label}
            value={values[key]}
            onChange={(value) => onChange({ [key]: value })}
            min={SILBATO_LIMITS[key][0]}
            max={SILBATO_LIMITS[key][1]}
            initialStep={step}
        />
    );

    return (
        <div className="mt-3 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
                <InputSpinner
                    label="Escala (×)"
                    value={values.scale}
                    onChange={(scale) => onChange({ scale })}
                    min={SILBATO_LIMITS.scale[0]}
                    max={SILBATO_LIMITS.scale[1]}
                    initialStep={0.05}
                />
                <button
                    type="button"
                    className={`btn btn-sm gap-2 self-end ${values.mirrored ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => onChange({ mirrored: !values.mirrored })}
                >
                    <FlipHorizontal2 className="h-4 w-4" />
                    {values.mirrored ? 'Espejado' : 'Sin espejar'}
                </button>
            </div>

            {SILBATO_SECTIONS.map(({ title, hint, fields }) => (
                <section key={title}>
                    <h4 className="text-xs font-semibold uppercase tracking-wide opacity-70">{title}</h4>
                    <p className="mt-0.5 text-xs opacity-55">{hint}</p>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                        {fields.map(({ key, label, step }) => spinner(key, label, step))}
                    </div>
                </section>
            ))}

            <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide opacity-70">Profundidad</h4>
                <p className="mt-0.5 text-xs opacity-55">
                    Igual que un imán: se vacía una banda intermedia y las lonchas de arriba y de
                    abajo quedan enteras. Son mm reales, no se escalan con la escala general.
                </p>
                <div className="mt-2 grid grid-cols-2 gap-3">
                    <InputSpinner
                        label="Alto del conducto (mm)"
                        value={values.ductMm}
                        onChange={(ductMm) => onChange({ ductMm })}
                        min={SILBATO_LIMITS.ductMm[0]}
                        max={totalThickness}
                        initialStep={0.1}
                    />
                    <InputSpinner
                        label="Suelo de cámara (mm)"
                        value={values.floorMm}
                        onChange={(floorMm) => onChange({ floorMm })}
                        min={0}
                        max={Math.max(totalThickness - values.ductMm, 0)}
                        initialStep={0.1}
                    />
                </div>
            </section>

            {index !== undefined && <SilbatoNote index={index} />}

            <p className={`rounded px-2 py-1.5 text-xs ${topCover < 0 ? 'bg-error/15 text-error' : 'bg-base-100 opacity-70'}`}>
                Huella: {geometry.widthMm.toFixed(1)} × {geometry.heightMm.toFixed(1)} mm.
                {' '}Material restante: {values.floorMm.toFixed(1)} mm abajo · {Math.max(topCover, 0).toFixed(1)} mm arriba
                {topCover < 0 && ' · No cabe en el grosor actual'}
            </p>
        </div>
    );
}

SilbatoFields.propTypes = {
    values: SILBATO_VALUE_SHAPE.isRequired,
    onChange: PropTypes.func.isRequired,
    totalThickness: PropTypes.number.isRequired,
    index: PropTypes.number,
};

const PROFILE_POINT_SHAPE = PropTypes.shape({ s: PropTypes.number, depthMm: PropTypes.number });

/**
 * Perfil del rebaje redondo visto de lado, editable arrastrando sus puntos.
 *
 * Izquierda, la arandela y el semicírculo de la tapa (s < 0); derecha, el
 * cuerpo hasta su extremo. Arriba está la cara trasera (profundidad 0) y abajo
 * la delantera. El hueco se pinta con el mismo degradado que el lienzo.
 *
 * Mientras se arrastra sólo cambia la gráfica: el elemento se reconstruye al
 * soltar, porque rehacer el grupo de fabric en cada movimiento va a tirones.
 */
function AbridorProfileEditor({ profile, smooth, bodyLengthMm, capRadiusMm, totalThickness, washer, onCommit, onSmoothChange }) {
    const svgRef = useRef(null);
    // Puede haber varios editores a la vez (borrador y cada abrebotellas): el
    // degradado necesita un id propio o todos pintarían con el del primero.
    const gradientId = `abridor-profile-${useId().replace(/:/g, '')}`;
    const [draft, setDraft] = useState(profile);
    const [dragging, setDragging] = useState(null);
    const [selected, setSelected] = useState(null);

    // Lo que llega de fuera manda salvo en mitad de un arrastre.
    const profileKey = JSON.stringify(profile);
    useEffect(() => {
        if (dragging === null) setDraft(profile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileKey]);

    const W = 300;
    const H = 150;
    const PAD = { left: 10, right: 10, top: 14, bottom: 18 };
    const sMin = -capRadiusMm;
    const sMax = Math.max(bodyLengthMm, 1);
    const x = (sValue) => PAD.left + ((sValue - sMin) / (sMax - sMin)) * (W - PAD.left - PAD.right);
    const y = (depth) => PAD.top + (depth / totalThickness) * (H - PAD.top - PAD.bottom);
    const round = (value) => Math.round(value * 10) / 10;

    const toModel = (event) => {
        const svg = svgRef.current;
        const point = svg.createSVGPoint();
        point.x = event.clientX;
        point.y = event.clientY;
        const local = point.matrixTransform(svg.getScreenCTM().inverse());
        return {
            s: sMin + ((local.x - PAD.left) / (W - PAD.left - PAD.right)) * (sMax - sMin),
            depthMm: ((local.y - PAD.top) / (H - PAD.top - PAD.bottom)) * totalThickness,
        };
    };

    const clampPoint = (points, index, { s: sValue, depthMm }) => {
        const low = index === 0 ? 0 : points[index - 1].s + 0.1;
        const high = index === points.length - 1 ? bodyLengthMm : points[index + 1].s - 0.1;
        return {
            s: round(Math.min(Math.max(sValue, low), Math.max(low, high))),
            depthMm: round(Math.min(Math.max(depthMm, 0), totalThickness)),
        };
    };

    const movePoint = (index, changes) => setDraft(points => points.map((point, i) => (
        i === index ? clampPoint(points, index, { ...point, ...changes }) : point
    )));

    const commit = (points) => onCommit(points.map(point => ({ ...point })));

    const onPointerMove = (event) => {
        if (dragging === null) return;
        movePoint(dragging, toModel(event));
    };
    const onPointerUp = () => {
        if (dragging === null) return;
        setDragging(null);
        commit(draft);
    };

    const addPoint = (event) => {
        if (draft.length >= ABRIDOR_PROFILE_POINTS[1]) return;
        const { s: sValue, depthMm } = toModel(event);
        if (sValue < 0 || sValue > bodyLengthMm) return;
        const point = { s: round(sValue), depthMm: round(Math.min(Math.max(depthMm, 0), totalThickness)) };
        if (draft.some(existing => Math.abs(existing.s - point.s) < 0.1)) return;
        const next = [...draft, point].sort((a, b) => a.s - b.s);
        setDraft(next);
        setSelected(next.indexOf(point));
        commit(next);
    };

    const removeSelected = () => {
        if (selected === null || draft.length <= ABRIDOR_PROFILE_POINTS[0]) return;
        const next = draft.filter((_, i) => i !== selected);
        setSelected(null);
        setDraft(next);
        commit(next);
    };

    // Lo que de verdad se vacía: los puntos unidos con rectas o con la curva,
    // muestreada igual que en el backend.
    const surface = sampleAbridorProfile(draft, smooth);
    // Para el degradado basta una parada por milímetro.
    const shading = smooth ? sampleAbridorProfile(draft, true, 1) : draft;
    const maxDepth = Math.max(...surface.map(point => point.depthMm), 0.001);
    const first = draft[0];
    const last = draft[draft.length - 1];
    const voidOutline = [
        [sMin, 0], [sMin, first.depthMm], [0, first.depthMm],
        ...surface.map(point => [point.s, point.depthMm]),
        [bodyLengthMm, last.depthMm], [bodyLengthMm, 0],
    ].map(([sValue, depth]) => `${x(sValue).toFixed(2)},${y(depth).toFixed(2)}`).join(' ');
    // La cresta es el punto interior más cercano a la superficie: el apoyo.
    const inner = draft.slice(1, -1);
    const crest = inner.length
        ? inner.reduce((best, point) => (point.depthMm < best.depthMm ? point : best))
        : null;
    const floorTop = Math.max(totalThickness - ABRIDOR_MIN_FLOOR_MM, 0);
    const selectedPoint = selected !== null ? draft[selected] : null;

    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Unión de los puntos">
                {[{ value: true, label: 'Curva' }, { value: false, label: 'Rectas' }].map(option => (
                    <button
                        key={option.label}
                        type="button"
                        className={`btn btn-xs ${smooth === option.value ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => onSmoothChange(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
            <svg
                ref={svgRef}
                viewBox={`0 0 ${W} ${H}`}
                className="w-full touch-none select-none rounded border border-base-300 bg-base-100"
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDoubleClick={addPoint}
                role="img"
                aria-label="Perfil de profundidades del rebaje"
            >
                <defs>
                    <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1={x(sMin)} x2={x(bodyLengthMm)} y1="0" y2="0">
                        <stop offset="0" stopColor={abridorDepthColor(first.depthMm, maxDepth)} />
                        <stop offset={(x(0) - x(sMin)) / (x(bodyLengthMm) - x(sMin))} stopColor={abridorDepthColor(first.depthMm, maxDepth)} />
                        {shading.map(point => (
                            <stop
                                key={point.s}
                                offset={(x(point.s) - x(sMin)) / (x(bodyLengthMm) - x(sMin))}
                                stopColor={abridorDepthColor(point.depthMm, maxDepth)}
                            />
                        ))}
                        <stop offset="1" stopColor={abridorDepthColor(last.depthMm, maxDepth)} />
                    </linearGradient>
                </defs>
                {/* La placa entera y, abajo, el suelo mínimo que debe quedar. */}
                <rect x={x(sMin)} y={y(0)} width={x(bodyLengthMm) - x(sMin)} height={y(totalThickness) - y(0)} fill="currentColor" opacity="0.12" />
                <rect x={x(sMin)} y={y(floorTop)} width={x(bodyLengthMm) - x(sMin)} height={y(totalThickness) - y(floorTop)} fill="currentColor" opacity="0.12" />
                <polygon points={voidOutline} fill={`url(#${gradientId})`} stroke="#7f1d1d" strokeWidth="1" />
                <line x1={x(0)} x2={x(0)} y1={y(0)} y2={y(totalThickness)} stroke="currentColor" strokeDasharray="3 3" opacity="0.35" />
                {/* La arandela entra desde la izquierda y asoma en el arco de la tapa. */}
                <rect
                    x={x(sMin) - 6}
                    y={y(washer.zMm)}
                    width={6 + (x(sMin + washer.protrusionMm) - x(sMin))}
                    height={Math.max(y(washer.zMm + washer.thicknessMm) - y(washer.zMm), 1.5)}
                    fill="#1f2937"
                />
                <text x={(x(sMin) + x(0)) / 2} y={H - 5} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.6">tapa</text>
                <text x={(x(0) + x(bodyLengthMm)) / 2} y={H - 5} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.6">cuerpo</text>
                {crest && (
                    <text x={x(crest.s)} y={Math.max(y(crest.depthMm) - 6, 9)} textAnchor="middle" fontSize="9" fill="currentColor">
                        apoyo {crest.depthMm.toFixed(1)}
                    </text>
                )}
                {draft.map((point, index) => (
                    <circle
                        key={index}
                        cx={x(point.s)}
                        cy={y(point.depthMm)}
                        r={selected === index ? 6 : 5}
                        fill={selected === index ? '#2563eb' : '#ffffff'}
                        stroke="#1f2937"
                        strokeWidth="1.5"
                        className="cursor-grab"
                        onPointerDown={(event) => {
                            event.stopPropagation();
                            event.currentTarget.ownerSVGElement.setPointerCapture?.(event.pointerId);
                            setSelected(index);
                            setDragging(index);
                        }}
                        onDoubleClick={(event) => event.stopPropagation()}
                    />
                ))}
            </svg>
            <p className="text-xs opacity-55">
                Arrastra los puntos: a lo largo, posición; arriba, más cerca de la cara trasera.
                Doble clic en la gráfica añade un punto.
            </p>
            {selectedPoint && (
                <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                        <InputSpinner
                            label="Posición (mm)"
                            value={selectedPoint.s}
                            onChange={(sValue) => {
                                const next = draft.map((point, i) => (i === selected ? clampPoint(draft, i, { ...point, s: sValue }) : point));
                                setDraft(next);
                                commit(next);
                            }}
                            min={0}
                            max={bodyLengthMm}
                            initialStep={0.5}
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <InputSpinner
                            label="Profundidad (mm)"
                            value={selectedPoint.depthMm}
                            onChange={(depthMm) => {
                                const next = draft.map((point, i) => (i === selected ? clampPoint(draft, i, { ...point, depthMm }) : point));
                                setDraft(next);
                                commit(next);
                            }}
                            min={0}
                            max={totalThickness}
                            initialStep={0.1}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost mb-0.5 text-error"
                        title="Quitar punto"
                        disabled={draft.length <= ABRIDOR_PROFILE_POINTS[0]}
                        onClick={removeSelected}
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            )}
        </div>
    );
}

AbridorProfileEditor.propTypes = {
    profile: PropTypes.arrayOf(PROFILE_POINT_SHAPE).isRequired,
    smooth: PropTypes.bool.isRequired,
    bodyLengthMm: PropTypes.number.isRequired,
    capRadiusMm: PropTypes.number.isRequired,
    totalThickness: PropTypes.number.isRequired,
    washer: PropTypes.shape({
        zMm: PropTypes.number,
        thicknessMm: PropTypes.number,
        protrusionMm: PropTypes.number,
    }).isRequired,
    onCommit: PropTypes.func.isRequired,
    onSmoothChange: PropTypes.func.isRequired,
};

/**
 * Cotas del abrebotellas. Todas son provisionales: el ancho, la rampa y lo que
 * asoma la arandela se ajustan imprimiendo y probando con tapas reales.
 */
function AbridorFields({ values, onChange, totalThickness }) {
    const geometry = useMemo(() => buildAbridorGeometry(values), [values]);
    const washer = geometry.washer;
    // `values` llega crudo del borrador; la geometría trae las cotas ya normalizadas.
    const params = geometry.params;
    const isRound = params.pocketShape === 'redondo';
    // En el recto el fondo es `depthMm`; en el redondo, el punto más hondo del
    // perfil, y junto a la arandela manda el primero.
    const deepest = isRound
        ? Math.max(...sampleAbridorProfile(params.profile, params.profileSmooth).map(point => point.depthMm))
        : params.depthMm;
    const washerSideDepth = isRound ? params.profile[0].depthMm : params.depthMm;
    const floor = totalThickness - deepest;
    const washerTop = params.washerZMm + washer.thicknessMm;
    const errors = [];
    const warnings = [];
    if (floor <= 0) errors.push('El rebaje atraviesa el llavero');
    else if (floor < ABRIDOR_MIN_FLOOR_MM) warnings.push(`Suelo fino (${floor.toFixed(1)} mm)`);
    if (washerTop > totalThickness) errors.push('La arandela no cabe en el grosor');
    if (params.washerZMm < ABRIDOR_MIN_COVER_MM) {
        warnings.push(`Poca tapa sobre la arandela (${params.washerZMm.toFixed(1)} mm): puede despegarse al hacer palanca`);
    }
    if (washerSideDepth < washerTop + 1) warnings.push('El rebaje no deja hueco bajo la arandela para la tapa');

    const spinner = (key, label, step, max = ABRIDOR_LIMITS[key][1]) => (
        <InputSpinner
            key={key}
            label={label}
            value={values[key]}
            onChange={(value) => onChange({ [key]: value })}
            min={ABRIDOR_LIMITS[key][0]}
            max={max}
            initialStep={step}
        />
    );

    return (
        <div className="mt-3 flex flex-col gap-4">
            <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide opacity-70">Arandela</h4>
                <p className="mt-0.5 text-xs opacity-55">
                    Se mete a mano en una pausa, como el imán. Lo que asoma dentro del rebaje es
                    lo que engancha la tapa.
                </p>
                <div className="mt-2 grid grid-cols-2 gap-3">
                    <label className="col-span-2 flex flex-col gap-1">
                        <span className="text-xs opacity-60">Tipo</span>
                        <select
                            className="select select-xs select-bordered"
                            value={values.washer}
                            onChange={(event) => onChange({ washer: event.target.value })}
                        >
                            {Object.entries(ABRIDOR_WASHERS).map(([key, { label, outerMm, innerMm, thicknessMm }]) => (
                                <option key={key} value={key}>
                                    {label} · {outerMm} × {innerMm} × {thicknessMm} mm
                                </option>
                            ))}
                        </select>
                    </label>
                    {spinner('protrusionMm', 'Asoma (mm)', 0.1, washer.outerMm / 2 - 0.5)}
                    {spinner('washerZMm', 'Desde atrás (mm)', 0.1, Math.max(totalThickness - washer.thicknessMm, 0))}
                </div>
            </section>

            <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide opacity-70">Rebaje para la tapa</h4>
                <div className="mt-2 grid grid-cols-2 gap-2" role="group" aria-label="Forma del rebaje">
                    {ABRIDOR_POCKET_SHAPES.map(shape => (
                        <button
                            key={shape}
                            type="button"
                            className={`btn btn-xs ${params.pocketShape === shape ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => onChange({ pocketShape: shape })}
                        >
                            {shape === 'redondo' ? 'Redondo con perfil' : 'Recto'}
                        </button>
                    ))}
                </div>
                {isRound ? (
                    <>
                        <p className="mt-2 text-xs opacity-55">
                            Abierto en la cara trasera. La boca es un semicírculo del tamaño de la
                            tapa junto a la arandela; el cuerpo sigue el perfil de abajo: rampa,
                            apoyo para hacer palanca y meseta final.
                        </p>
                        <div className="mt-2 grid grid-cols-2 gap-3">
                            {spinner('capDiameterMm', 'Ø de la tapa (mm)', 0.5)}
                            {spinner('bodyLengthMm', 'Largo del cuerpo (mm)', 0.5)}
                        </div>
                        <div className="mt-3">
                            <AbridorProfileEditor
                                profile={params.profile}
                                smooth={params.profileSmooth}
                                onSmoothChange={(profileSmooth) => onChange({ profileSmooth })}
                                bodyLengthMm={params.bodyLengthMm}
                                capRadiusMm={params.capDiameterMm / 2}
                                totalThickness={totalThickness}
                                washer={{ zMm: params.washerZMm, thicknessMm: washer.thicknessMm, protrusionMm: params.protrusionMm }}
                                onCommit={(profile) => onChange({ profile })}
                            />
                        </div>
                    </>
                ) : (
                <>
                <p className="mt-2 text-xs opacity-55">
                    Abierto en la cara trasera: hondo junto a la arandela y en rampa hasta la
                    superficie en el otro extremo, para que la tapa entre inclinada.
                </p>
                {/* Las mismas muestras que pinta el lienzo, para saber qué tramo es cuál. */}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-70">
                    <span className="flex items-center gap-1.5">
                        <span className="h-3 w-5 rounded-sm" style={{ background: ABRIDOR_POCKET_COLORS.flat }} />
                        Tramo hondo, junto a la arandela
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span
                            className="h-3 w-5 rounded-sm"
                            style={{ background: `linear-gradient(to right, ${ABRIDOR_POCKET_COLORS.rampDeep}, ${ABRIDOR_POCKET_COLORS.rampSurface})` }}
                        />
                        Rampa, hasta la superficie
                    </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3">
                    {spinner('pocketWidthMm', 'Ancho (mm)', 0.5)}
                    {spinner('depthMm', 'Profundidad (mm)', 0.1, totalThickness)}
                    {spinner('flatMm', 'Tramo hondo (mm)', 0.5)}
                    {spinner('rampMm', 'Rampa (mm)', 0.5)}
                </div>
                </>
                )}
            </section>

            <p className={`rounded px-2 py-1.5 text-xs ${errors.length ? 'bg-error/15 text-error' : warnings.length ? 'bg-warning/15 text-warning' : 'bg-base-100 opacity-70'}`}>
                Huella: {geometry.widthMm.toFixed(1)} × {geometry.heightMm.toFixed(1)} mm.
                {' '}Suelo bajo el rebaje: {Math.max(floor, 0).toFixed(1)} mm
                {[...errors, ...warnings].map(message => ` · ${message}`)}
            </p>
        </div>
    );
}

AbridorFields.propTypes = {
    values: PropTypes.shape({
        pocketShape: PropTypes.string,
        washer: PropTypes.string,
        protrusionMm: PropTypes.number,
        capDiameterMm: PropTypes.number,
        bodyLengthMm: PropTypes.number,
        profile: PropTypes.arrayOf(PROFILE_POINT_SHAPE),
        profileSmooth: PropTypes.bool,
        pocketWidthMm: PropTypes.number,
        flatMm: PropTypes.number,
        rampMm: PropTypes.number,
        depthMm: PropTypes.number,
        washerZMm: PropTypes.number,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    totalThickness: PropTypes.number.isRequired,
};

// Lo que dice el editor de dónde está la huella respecto al borde del llavero.
// El backend ignora el abrelatas en los tres casos, así que se avisa antes.
const ABRELATAS_EDGE_ERRORS = {
    inside: 'No llega al borde: arrástralo hasta que la zona gris quede fuera del llavero',
    outside: 'Está fuera del llavero',
    crossing: 'Atraviesa el llavero de lado a lado: tiene que salir solo por un canto',
};

function AbrelatasFields({ values, onChange, totalThickness, edgeStatus }) {
    const geometry = useMemo(() => buildAbrelatasGeometry(values), [values]);
    // `values` llega crudo del borrador; la geometría trae las cotas ya normalizadas.
    const params = geometry.params;
    const isWedge = params.openerShape === 'cuna';
    const edgeName = isWedge ? 'Filo' : 'Labio';
    const roof = totalThickness - params.edgeMm - params.slotMm;
    const errors = [];
    const warnings = [];
    if (ABRELATAS_EDGE_ERRORS[edgeStatus]) errors.push(ABRELATAS_EDGE_ERRORS[edgeStatus]);
    if (isWedge && params.edgeMm >= totalThickness) errors.push('El filo no es más fino que el llavero');
    if (!isWedge && roof <= 0) errors.push('La ranura no cabe en el grosor');
    else if (!isWedge && roof < ABRELATAS_MIN_ROOF_MM) warnings.push(`Techo fino (${roof.toFixed(1)} mm)`);
    if (params.edgeMm < ABRELATAS_MIN_EDGE_MM) {
        warnings.push(`${edgeName} de ${params.edgeMm.toFixed(1)} mm: puede romperse o doblarse`);
    }
    const angle = Math.atan2(Math.max(totalThickness - params.edgeMm, 0), params.rampMm) * 180 / Math.PI;

    const spinner = (key, label, step, max = ABRELATAS_LIMITS[key][1]) => (
        <InputSpinner
            key={key}
            label={label}
            value={values[key] ?? params[key]}
            onChange={(value) => onChange({ [key]: value })}
            min={ABRELATAS_LIMITS[key][0]}
            max={max}
            initialStep={step}
        />
    );

    return (
        <div className="mt-3 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Forma del abrelatas">
                {ABRELATAS_SHAPES.map(shape => (
                    <button
                        key={shape}
                        type="button"
                        className={`btn btn-xs ${params.openerShape === shape ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => onChange({ openerShape: shape })}
                    >
                        {shape === 'cuna' ? 'Cuña' : 'Ranura'}
                    </button>
                ))}
            </div>
            <p className="text-xs opacity-55">
                {isWedge
                    ? 'La placa se afina por detrás en rampa hasta un filo fino en el canto, que se mete bajo la anilla.'
                    : 'Una ranura cerrada por las dos caras: el labio de delante, fino en el canto, se mete bajo la anilla y engorda en rampa hacia dentro, y el techo la retiene.'}
                {' '}Colócalo rebasando el borde: las medidas se toman desde el canto, y ahí el
                redondeo no se aplica para no comerse el filo.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-70">
                <span className="flex items-center gap-1.5">
                    <span className="h-3 w-5 rounded-sm" style={{ background: ABRELATAS_OVERSHOOT_COLOR }} />
                    Fuera del llavero
                </span>
                <span className="flex items-center gap-1.5">
                    <span
                        className="h-3 w-5 rounded-sm"
                        style={{ background: `linear-gradient(to right, ${abridorDepthColor(1, 1)}, ${abridorDepthColor(0, 1)})` }}
                    />
                    {isWedge ? 'Rampa, honda junto al canto' : 'Ranura, más alta en la boca'}
                </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
                {spinner('edgeMm', `${edgeName} (mm)`, 0.2, Math.min(ABRELATAS_LIMITS.edgeMm[1], totalThickness))}
                {spinner('widthMm', 'Ancho (mm)', 0.5)}
                {isWedge ? spinner('rampMm', 'Rampa (mm)', 0.5) : (
                    <>
                        {spinner('slotMm', 'Alto en la boca (mm)', 0.1)}
                        {spinner('slotEndMm', 'Alto en el fondo (mm)', 0.1, params.slotMm)}
                        {spinner('reachMm', 'Hondo (mm)', 0.5)}
                    </>
                )}
            </div>
            <p className={`rounded px-2 py-1.5 text-xs ${errors.length ? 'bg-error/15 text-error' : warnings.length ? 'bg-warning/15 text-warning' : 'bg-base-100 opacity-70'}`}>
                Huella: {geometry.widthMm.toFixed(1)} × {geometry.heightMm.toFixed(1)} mm.
                {' '}{isWedge ? `Rampa a ${angle.toFixed(0)}°` : `Techo: ${Math.max(roof, 0).toFixed(1)} mm`}
                {[...errors, ...warnings].map(message => ` · ${message}`)}
            </p>
        </div>
    );
}

AbrelatasFields.propTypes = {
    values: PropTypes.shape({
        openerShape: PropTypes.string,
        edgeMm: PropTypes.number,
        rampMm: PropTypes.number,
        slotMm: PropTypes.number,
        slotEndMm: PropTypes.number,
        reachMm: PropTypes.number,
        widthMm: PropTypes.number,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    totalThickness: PropTypes.number.isRequired,
    edgeStatus: PropTypes.string,
};

const CROCS_FIELDS = [
    { key: 'headDiameterMm', label: 'Cabeza Ø', min: 6, max: 20, step: 0.5 },
    { key: 'neckDiameterMm', label: 'Cuello Ø', min: 2, max: 12, step: 0.2 },
    { key: 'neckLengthMm', label: 'Largo del cuello', min: 1, max: 10, step: 0.5 },
    { key: 'headThicknessMm', label: 'Grosor cabeza', min: 0.8, max: 5, step: 0.1 },
];

const CROCS_MODES = [
    { value: 'i', label: 'Integrado' },
    { value: 'a', label: 'Aparte' },
];

/**
 * Pines de Crocs: una seta en la cara de atrás que se mete en un agujero de la
 * Croc. Como la ranura y el tornillo, cada pin es un marcador que se arrastra
 * por el lienzo y lleva sus cotas en el id; aquí sólo se añaden y se ajustan.
 */
function CrocsPinFields() {
    const { canvas, getCrocsPins, onAddCrocsPin, onUpdateCrocsPin, onRemoveCrocsPin } = useTools();
    const [pins, setPins] = useState([]);
    const [mode, setMode] = useState('i');

    const refresh = () => setPins(getCrocsPins?.() ?? []);

    useEffect(() => {
        if (!canvas) return undefined;
        refresh();
        const events = ['object:added', 'object:removed', 'object:modified'];
        events.forEach(event => canvas.on(event, refresh));
        return () => events.forEach(event => canvas.off(event, refresh));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas]);

    return (
        <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Cómo se fabrica el pin">
                {CROCS_MODES.map(option => (
                    <button
                        key={option.value}
                        type="button"
                        className={`btn btn-sm ${mode === option.value ? 'ring-2 ring-primary border-primary' : 'btn-ghost border border-base-300'}`}
                        onClick={() => setMode(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
            <button type="button" className="btn btn-primary btn-sm w-full" onClick={() => onAddCrocsPin?.({ mode })}>
                Añadir pin {mode === 'a' ? 'aparte' : 'integrado'}
            </button>

            {pins.map(pin => (
                // La clave es el número del pin: el id lleva las cotas y
                // remontaría la fila en mitad de la edición.
                <article key={pin.index} className="rounded border border-base-300 bg-base-200 p-3">
                    <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm font-semibold">
                            <Footprints className="h-4 w-4 text-success" /> Pin {pin.index}
                        </span>
                        <button
                            type="button"
                            className="btn btn-xs btn-circle btn-ghost text-error"
                            title="Eliminar pin"
                            onClick={() => onRemoveCrocsPin?.(pin.id)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                    <select
                        className="select select-xs select-bordered mt-2 w-full"
                        value={pin.mode}
                        aria-label="Cómo se fabrica el pin"
                        onChange={(event) => onUpdateCrocsPin?.(pin.id, { mode: event.target.value })}
                    >
                        <option value="i">Integrado en la pieza</option>
                        <option value="a">Aparte, encajado a presión</option>
                    </select>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        {CROCS_FIELDS.map(({ key, label, min, max, step }) => {
                            // Se aplica al salir del campo: cada cambio rehace el
                            // marcador y un campo controlado pelearía con lo tecleado.
                            const commit = (event) => {
                                const next = Number(event.target.value);
                                if (!Number.isFinite(next) || next < min || next > max) {
                                    event.target.value = pin[key];
                                    return;
                                }
                                if (next !== pin[key]) onUpdateCrocsPin?.(pin.id, { [key]: next });
                            };
                            return (
                                <label key={key} className="flex flex-col gap-1">
                                    <span className="text-[11px] opacity-60">{label} (mm)</span>
                                    <input
                                        key={pin.id}
                                        type="number"
                                        className="input input-xs input-bordered w-full"
                                        defaultValue={pin[key]}
                                        min={min}
                                        max={max}
                                        step={step}
                                        onBlur={commit}
                                        onKeyDown={(event) => { if (event.key === 'Enter') event.target.blur(); }}
                                    />
                                </label>
                            );
                        })}
                    </div>
                </article>
            ))}

            <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs leading-snug">
                El pin sale de la <strong>cara de atrás</strong> y se mete en un agujero de la Croc: el
                cuello tiene que medir lo que el grosor de la tira y la cabeza ser más ancha que el
                agujero. <strong>Integrado</strong> sale en una pieza con el diseño; la parte de abajo de
                la cabeza va en cono a 45° para imprimirse sin soportes. <strong>Aparte</strong> deja un
                agujero ciego en la pieza y el pin sale suelto en la cama, de pie sobre su cabeza plana,
                para encajarlo a presión (o con una gota de pegamento). Arrastra el marcador para
                colocarlo; tiene que quedar entero dentro de la pieza.
            </div>
        </div>
    );
}

// `hangingPanel` son las ranuras y los tornillos de colgado: no son elementos
// internos (no van en `insert_*` ni en la lista de abajo), pero se eligen desde
// los mismos botones de tipo para que se encuentren donde se busca todo lo demás.
export default function InternalElementsPanel({ hangingPanel = null, showCrocs = false }) {
    const {
        canvas,
        getInternalElements,
        onAddInternalElements,
        onUpdateInternalElement,
        onRemoveInternalElement,
    } = useTools();
    const dataModelo3d = useDataModelo3dContext();
    const totalThickness = Math.max(Number(dataModelo3d?.thiknessModelo3d) || 6, 0.1);
    const [elements, setElements] = useState([]);
    const [kind, setKind] = useState('magnet');
    const [shape, setShape] = useState('circle');
    const [quantity, setQuantity] = useState(1);
    const [draftLocked, setDraftLocked] = useState(true);
    // Candado de los elementos ya añadidos, por índice (el id cambia al editar).
    // Sin tocar, queda bloqueado sólo si la pieza es cuadrada.
    const [unlocked, setUnlocked] = useState({});
    // Un borrador por tipo, no uno compartido: cada tipo tiene sus propios
    // campos, y con un solo borrador el render que sigue a cambiar de tipo se
    // pintaba con las cotas del anterior (el efecto que las reponía llegaba un
    // render tarde). Además conserva lo que el usuario llevara escrito al
    // volver a un tipo que ya había tocado.
    const [drafts, setDrafts] = useState(PRESETS);
    const draft = drafts[kind];
    const patchDraft = (changes) => setDrafts(current => ({
        ...current,
        [kind]: { ...current[kind], ...changes },
    }));

    // Ni el colgado ni el pin son elementos internos: no usan el formulario
    // de abajo ni salen en la lista.
    const isHanging = kind === 'colgado' || kind === 'crocs';
    const isSilbato = kind === 'silbato';
    const isAssembly = ASSEMBLY_KINDS.has(kind);

    const refresh = () => setElements(getInternalElements?.() ?? []);

    useEffect(() => {
        if (!canvas) return undefined;
        refresh();
        const events = ['object:added', 'object:removed', 'object:modified', 'layer:changed'];
        events.forEach(event => canvas.on(event, refresh));
        return () => events.forEach(event => canvas.off(event, refresh));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas]);

    const countLabel = useMemo(() => (
        elements.length === 1 ? '1 elemento interno' : `${elements.length} elementos internos`
    ), [elements.length]);

    const update = (element, changes) => {
        onUpdateInternalElement?.(element.id, changes);
        requestAnimationFrame(refresh);
    };

    const add = () => {
        onAddInternalElements?.({ kind, shape, quantity, ...draft });
        requestAnimationFrame(refresh);
    };

    return (
        <div className="flex flex-col gap-4 pb-5">
            <section className="border-b border-base-300 pb-4">
                <div className="grid grid-cols-2 gap-2" role="group" aria-label="Tipo de elemento">
                    {[
                        { value: 'magnet', label: 'Imán', Icon: Magnet },
                        { value: 'nfc', label: 'NFC', Icon: Radio },
                        { value: 'silbato', label: 'Silbato', Icon: Wind },
                        { value: 'abridor', label: 'Abrebotellas', Icon: Beer },
                        { value: 'abrelatas', label: 'Abrelatas', Icon: CupSoda },
                        ...(hangingPanel ? [{ value: 'colgado', label: 'Colgado', Icon: Hammer }] : []),
                        ...(showCrocs ? [{ value: 'crocs', label: 'Pin Crocs', Icon: Footprints }] : []),
                    ].map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            type="button"
                            className={`btn btn-sm gap-2 ${kind === value ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => setKind(value)}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </button>
                    ))}
                </div>

                {kind === 'colgado' && <div className="mt-4">{hangingPanel}</div>}
                {kind === 'crocs' && <CrocsPinFields />}
                {isSilbato && (
                    <SilbatoFields
                        values={draft}
                        totalThickness={totalThickness}
                        onChange={patchDraft}
                    />
                )}
                {kind === 'abridor' && (
                    <AbridorFields
                        values={draft}
                        totalThickness={totalThickness}
                        onChange={patchDraft}
                    />
                )}
                {kind === 'abrelatas' && (
                    <AbrelatasFields
                        values={draft}
                        totalThickness={totalThickness}
                        onChange={patchDraft}
                    />
                )}
                {!isAssembly && !isHanging && (
                <>
                <div className="mt-3 grid grid-cols-2 gap-2" role="group" aria-label="Forma">
                    {[
                        { value: 'circle', label: 'Circular', Icon: Circle },
                        { value: 'square', label: 'Cuadrado', Icon: Square },
                    ].map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            type="button"
                            className={`btn btn-sm gap-2 ${shape === value ? 'ring-2 ring-primary border-primary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => setShape(value)}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </button>
                    ))}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                    <SizeFields
                        shape={shape}
                        width={draft.sizeMm}
                        height={draft.heightMm ?? draft.sizeMm}
                        locked={draftLocked}
                        onLockChange={setDraftLocked}
                        onChange={patchDraft}
                    />
                    <InputSpinner
                        label="Cantidad"
                        value={quantity}
                        onChange={setQuantity}
                        min={1}
                        max={20}
                        integer
                        initialStep={1}
                    />
                    <InputSpinner
                        label="Grosor (mm)"
                        value={draft.thicknessMm}
                        onChange={(thicknessMm) => patchDraft({ thicknessMm })}
                        min={0.2}
                        max={totalThickness}
                        initialStep={0.1}
                    />
                    <InputSpinner
                        label="Desde abajo (mm)"
                        value={draft.bottomOffsetMm}
                        onChange={(bottomOffsetMm) => patchDraft({ bottomOffsetMm })}
                        min={0}
                        max={Math.max(totalThickness - draft.thicknessMm, 0)}
                        initialStep={0.1}
                    />
                </div>
                </>
                )}

                {!isHanging && (
                    <button type="button" className="btn btn-primary btn-sm mt-4 w-full" onClick={add}>
                        Añadir {!isAssembly && quantity > 1 ? `${quantity} elementos` : labelFor({ kind })}
                    </button>
                )}
            </section>

            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{countLabel}</span>
                <span className="text-xs opacity-55">Grosor total: {totalThickness} mm</span>
            </div>

            {elements.length === 0 && (
                <p className="rounded border border-dashed border-base-300 p-3 text-center text-sm opacity-60">
                    Todavía no hay imanes, NFC, silbatos, abrebotellas ni abrelatas en el diseño.
                </p>
            )}

            {elements.map((element) => {
                // El silbato y el abrebotellas llevan su propio aviso de cotas.
                const assembly = ASSEMBLY_KINDS.has(element.kind);
                const topCover = assembly
                    ? 0
                    : totalThickness - element.bottomOffsetMm - element.thicknessMm;
                const invalid = topCover < 0;
                const thinCover = !invalid && !assembly
                    && Math.min(topCover, element.bottomOffsetMm) < 0.6;
                return (
                    <article key={element.id} className="rounded border border-base-300 bg-base-200 p-3">
                        <div className="flex items-center gap-2">
                            {element.kind === 'nfc' && <Radio className="h-4 w-4 text-info" />}
                            {element.kind === 'silbato' && <Wind className="h-4 w-4 text-secondary" />}
                            {element.kind === 'abridor' && <Beer className="h-4 w-4 text-error" />}
                            {element.kind === 'abrelatas' && <CupSoda className="h-4 w-4 text-warning" />}
                            {element.kind === 'magnet' && <Magnet className="h-4 w-4" />}
                            <input
                                className="input input-xs input-bordered min-w-0 flex-1"
                                defaultValue={element.name || `${labelFor(element)} ${element.index}`}
                                aria-label="Nombre del elemento"
                                onBlur={(event) => {
                                    const name = event.target.value.trim() || `${labelFor(element)} ${element.index}`;
                                    if (name !== element.name) update(element, { name });
                                }}
                                onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                            />
                            <button
                                type="button"
                                className="btn btn-xs btn-circle btn-ghost text-error"
                                title="Eliminar elemento"
                                onClick={() => onRemoveInternalElement?.(element.id)}
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>

                        {element.kind === 'silbato' && (
                            <SilbatoFields
                                values={element}
                                index={element.index}
                                totalThickness={totalThickness}
                                onChange={(changes) => update(element, changes)}
                            />
                        )}
                        {element.kind === 'abridor' && (
                            <AbridorFields
                                values={element}
                                totalThickness={totalThickness}
                                onChange={(changes) => update(element, changes)}
                            />
                        )}
                        {element.kind === 'abrelatas' && (
                            <AbrelatasFields
                                values={element}
                                totalThickness={totalThickness}
                                edgeStatus={element.edgeStatus}
                                onChange={(changes) => update(element, changes)}
                            />
                        )}
                        {!assembly && (
                            <>
                                <div className="mt-3 grid grid-cols-2 gap-3">
                                    <label className="flex flex-col gap-1">
                                        <span className="text-xs opacity-60">Forma</span>
                                        <select
                                            className="select select-xs select-bordered"
                                            value={element.shape}
                                            onChange={(event) => update(element, { shape: event.target.value })}
                                        >
                                            <option value="circle">Circular</option>
                                            <option value="square">Cuadrado</option>
                                        </select>
                                    </label>
                                    <SizeFields
                                        shape={element.shape}
                                        width={element.sizeMm}
                                        height={element.heightMm}
                                        locked={unlocked[element.index] === undefined
                                            ? element.sizeMm === element.heightMm
                                            : !unlocked[element.index]}
                                        onLockChange={(locked) => setUnlocked(current => ({ ...current, [element.index]: !locked }))}
                                        onChange={(changes) => update(element, changes)}
                                    />
                                    <InputSpinner
                                        label="Grosor (mm)"
                                        value={element.thicknessMm}
                                        onChange={(thicknessMm) => update(element, { thicknessMm })}
                                        min={0.2}
                                        max={totalThickness}
                                        initialStep={0.1}
                                    />
                                    <InputSpinner
                                        label="Desde abajo (mm)"
                                        value={element.bottomOffsetMm}
                                        onChange={(bottomOffsetMm) => update(element, { bottomOffsetMm })}
                                        min={0}
                                        max={Math.max(totalThickness - element.thicknessMm, 0)}
                                        initialStep={0.1}
                                    />
                                </div>

                                <div className={`mt-3 rounded px-2 py-1.5 text-xs ${invalid ? 'bg-error/15 text-error' : thinCover ? 'bg-warning/15 text-warning' : 'bg-base-100'}`}>
                                    Material restante: {element.bottomOffsetMm.toFixed(1)} mm abajo · {Math.max(topCover, 0).toFixed(1)} mm arriba
                                    {invalid && ' · No cabe en el grosor actual'}
                                    {thinCover && ' · Recubrimiento fino'}
                                </div>
                            </>
                        )}
                    </article>
                );
            })}
        </div>
    );
}

InternalElementsPanel.propTypes = {
    hangingPanel: PropTypes.node,
    showCrocs: PropTypes.bool,
};
